'use client';

import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { useState } from "react";

/**
 * @typedef {import("@prismicio/client").Content.ContactFormSlice} ContactFormSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ContactFormSlice>} ContactFormProps
 * @type {import("react").FC<ContactFormProps>}
 */
const ContactForm = ({ slice }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    consent: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };
    setFormData(updatedFormData);

    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const toggleIsButtonHovered = (value) => {
    console.log('value', value);
    setIsButtonHovered(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom/prénom est requis';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'L\'entreprise est requise';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    }
    
    if (!formData.consent) {
      newErrors.consent = 'Vous devez accepter les conditions';
    }

    // If there are errors, show them and don't submit
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitStatus({ type: 'error', message: 'Veuillez remplir tous les champs requis.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrors({});

    try {
      const response = await fetch('/api/ppdrv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erreur lors de l\'envoi');
      }

      setSubmitStatus({ type: 'success', message: data.message || 'Votre message a été envoyé avec succès !' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: '',
        consent: false
      });
    } catch (error) {
      console.log('error', error);
      console.error('Erreur:', error);
      setSubmitStatus({ type: 'error', message: 'Une erreur est survenue. Veuillez réessayer.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      id={slice.primary.anchor_id || undefined}
      className="theme-beige pt-8 pb-25 md:px-20 px-5 md:py-25 flex flex-col lg:flex-row items-start justify-between gap-10 md:gap-18"
    >
      <div className="w-full lg:w-1/2">
        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({ children }) => <h2 className="h2 mb-3">{children}</h2>,
            }}
          />
        )}

        {isFilled.richText(slice.primary.description) && (
          <PrismicRichText
            field={slice.primary.description}
            components={{
              paragraph: ({ children }) => <p className="p last:mb-0">{children}</p>,
              strong: ({ children }) => <strong className="font-bold">{children}</strong>,
              hyperlink: ({ children, node }) => (
                <a href={node.data.url} target={node.data.target} className="underline-moyam">
                  {children}
                </a>
              ),
            }}
          />
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full lg:w-1/2">
        <div className="flex flex-col md:flex-row gap-4 md:gap-6">
          <div className="flex flex-col gap-2 w-full">
            <label htmlFor="name" className="block label">
              Nom/Prénom*
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Robert Paris"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2 md:py-3 rounded-full border-1 text-dark-pink focus:outline-none ${
                errors.name ? 'border-red-500' : 'border-dark-pink'
              } ${formData.name ? 'bg-white' : 'bg-beige'}`}
            />
            {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
          </div>
          <div className="flex flex-col gap-2 w-full">
            <label htmlFor="company" className="block label">
              Entreprise*
            </label>
            <input
              type="text"
              id="company"
              placeholder="Entreprise"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className={`w-full px-4 py-2 md:py-3 rounded-full border-1 text-dark-pink focus:outline-none ${
                errors.company ? 'border-red-500' : 'border-dark-pink'
              } ${formData.company ? 'bg-white' : 'bg-beige'}`}
            />
            {errors.company && <p className="text-red-500 text-sm">{errors.company}</p>}
          </div>
        </div>

        <div className="flex flex-col gap-2 w-full">
          <label htmlFor="email" className="block label">
            Email*
          </label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="contact@contact.com"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 md:py-3 rounded-full border-1 text-dark-pink focus:outline-none ${
              errors.email ? 'border-red-500' : 'border-dark-pink'
            } ${formData.email ? 'bg-white' : 'bg-beige'}`}
          />
          {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
        </div>

        <div className="flex flex-col gap-2 w-full">
          <label htmlFor="phone" className="block label">
            Téléphone*
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            placeholder="+33 6 01 02 03 04"
            value={formData.phone}
            onChange={handleChange}
            className={`w-full px-4 py-2 md:py-3 rounded-full border-1 text-dark-pink focus:outline-none ${
              errors.phone ? 'border-red-500' : 'border-dark-pink'
            } ${formData.phone ? 'bg-white' : 'bg-beige'}`}
          />
          {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="message" className="block label">
            Message*
          </label>
          <textarea
            id="message"
            name="message"
            placeholder="Bonjour, 
Nous sommes intéressés par vos produits, pouvons-nous prendre rendez-vous pour en discuter ? Bien cordialement, 
Robert Paris "
            value={formData.message}
            onChange={handleChange}
            rows={6}
            className={`w-full px-4 py-2 md:py-3 rounded-3xl border-1 text-dark-pink focus:outline-none ${
              errors.message ? 'border-red-500' : 'border-dark-pink'
            } ${formData.message ? 'bg-white' : 'bg-beige'}`}
          />
          {errors.message && <p className="text-red-500 text-sm">{errors.message}</p>}
        </div>

        {isFilled.keyText(slice.primary.consent_label) && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-start gap-3">
              <label htmlFor="consent" className="flex md:items-center gap-3 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    id="consent"
                    name="consent"
                    checked={formData.consent}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className={`translate-y-[2px] md:translate-0 w-5 h-5 md:w-6 md:h-6 border-1 rounded bg-beige peer-checked:bg-white flex items-center justify-center transition-colors ${
                    errors.consent ? 'border-red-500' : 'border-dark-pink'
                  }`}>
                    {formData.consent && (
                      <img src="/img/check-icon.svg" alt="Checked" className="w-3 h-3 md:w-4 md:h-4" />
                    )}
                  </div>
                </div>
                <PrismicRichText
                  field={slice.primary.consent_label}
                  components={{
                    paragraph: ({ children }) => <p className="font-body text-base md:text-xl">{children}</p>,
                    strong: ({ children }) => <strong className="font-body text-base md:text-xl font-bold">{children}</strong>,
                    hyperlink: ({ children, node }) => (
                      <a href={node.data.url} target={node.data.target} className="underline-moyam">
                        {children}
                      </a>
                    ),
                  }}
                />
              </label>
            </div>
            {errors.consent && <p className="text-red-500 text-sm">{errors.consent}</p>}
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          onMouseOver={() => toggleIsButtonHovered(true)}
          onMouseLeave={() => toggleIsButtonHovered(false)}
          className={`cta cta-moyam-orange mt-5 self-end flex items-center gap-3 ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <img src={isButtonHovered ? "/img/paper-plane-orange.svg" : "/img/paper-plane.svg"} alt="Check" />
          {isSubmitting
            ? 'Envoi en cours...'
            : isFilled.keyText(slice.primary.submit_label)
              ? slice.primary.submit_label
              : 'Envoyer'}
        </button>

        {submitStatus && (
          <div className={`w-full mt-4 px-4 py-3 rounded-3xl border text-center ${submitStatus.type === 'success'
            ? 'bg-light-green text-dark-green border-dark-green'
            : 'bg-light-pink text-dark-pink border-dark-pink'
            }`}>
            <p className="font-body text-base md:text-lg">
              {submitStatus.message}
            </p>
          </div>
        )}
      </form>
    </section>
  );
};

export default ContactForm;
